<script setup lang="ts">
// oxlint-disable no-console
/* oxlint-disable-file no-console */
import {
  GitlabFeatureFlags,
  type DropDownItem,
  type ExclusionUpdate,
  type PaAPI,
  type WorkersApiLegacy,
} from '@rialtic/types'
import { isWithinInterval } from 'date-fns'
import { intlDateFormats, PERMISSIONS, WORKSPACE_PERMISSION } from '#imports'
import {
  ALL_LOB,
  draftOperations,
  draftOptions,
  exclusionStatus,
} from '~/constants'
import { useWorkspace } from '~/stores/workspace'
import type { ExclusionTableRow } from '../../exclusions.vue'

definePageMeta({
  key: (route) => route.fullPath,
  isDetailView: true,
  middleware: 'connector-required',
})

const props = defineProps<{
  exclusions: ExclusionTableRow[]
}>()

const appConfigUnleashed = useAppConfigurationUnleashed()

const isExclusionDuplicateEnabled = computed(() =>
  appConfigUnleashed.isUnleashFeatureEnabled(
    GitlabFeatureFlags.featureEnableExclusionDuplicate,
  ),
)

const { activePreset } = useDatePresets()

const { $groundcover, $toast, $pendo } = useNuxtApp()

const route = useRoute<'exclusions-id'>()

const router = useRouter()

const basePath = computed(() =>
  route.path.startsWith('/dashboard') ? '/dashboard/exclusions' : '/exclusions',
)

const workspace = useWorkspace()

const workspaceTimezone = useWorkspaceTimezone()

const analyticsTag = (tag: string) =>
  `${tag}:${workspace.connectorId === ALL_LOB ? ALL_LOB : 'single_lob'}`

const exclusionState = useActiveExclusion()

const workersApi = useWorkersApi()

const { setUrlParams } = useUrlParamsState()

const { todayDate } = useClock()

const draftSelection = ref<DropDownItem>({} as DropDownItem)

const selectedVersion = ref<DropDownItem>({} as DropDownItem)

const confirmDraftDelete = ref(false)

const confirmCreateExclusion = ref(false)

const confirmModifyDialogOpen = ref(false)

const confirmNameEdit = ref(false)

const confirmCategoryEdit = ref(false)

const confirmScheduledVersionDelete = ref(false)

const statusEditorOpen = ref(false)

const isLoading = ref(false)

const dialogs: Record<string, Ref> = {
  confirmDraftDelete,
  confirmCreateExclusion,
  confirmModifyDialogOpen,
  confirmNameEdit,
  confirmCategoryEdit,
  confirmScheduledVersionDelete,
  statusEditorOpen,
}

// we want to omit creating exclusion for new exclusions
if (draftOptions.some((option) => option.label.includes('Create')))
  draftOptions.shift()

const exclusionId = computed(() => {
  const id = route?.params.id

  return Array.isArray(id) ? id[0] : id
})

const getExclusionId = () => {
  const rawId = exclusionId.value

  return Array.isArray(rawId) ? rawId[0] : rawId
}

const params = computed(() => ({
  ...(selectedVersion.value &&
    (Object.keys(selectedVersion.value).length
      ? { version: selectedVersion.value.itemValue }
      : {})),
  include: true,
}))

const {
  data: exclusion,
  refresh,
  status,
} = useApiFetch<
  PaAPI.Get['/exclusion/{id}'] & {
    id: string
    versions?: {
      version: number
      version_start_date: string
      version_end_date?: string
    }[]
    hasFutureVersion?: boolean
    category?: { id: string; name: string }
  }
>(() => `/api/paapi/exclusion/${exclusionId.value}`, {
  params,
  watch: false,
  transform: (exclusion) => {
    const dateOnly = (dateString: string) => dateString.split('T')[0]

    if (exclusion.effective_date_start) {
      exclusion.effective_date_start = dateOnly(
        exclusion.effective_date_start as string,
      )
    }

    if (exclusion.effective_date_end) {
      exclusion.effective_date_end = dateOnly(
        exclusion.effective_date_end as string,
      )
    }

    return exclusion
  },
})

const formatDate = (date: string) =>
  workspaceTimezone.format(date, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })

const versionItems = computed<DropDownItem[]>(() => {
  return exclusion.value?.versions?.length
    ? exclusion.value.versions
        ?.map(
          (version: {
            version: number
            version_start_date: string
            version_end_date?: string
          }) => {
            return {
              label: `(${formatDate(version.version_start_date)} - ${
                version.version_end_date
                  ? formatDate(version.version_end_date)
                  : 'Forward'
              })`,
              itemValue: version.version,
            }
          },
        )
        .sort(
          (a: { itemValue: number }, b: { itemValue: number }) =>
            b.itemValue - a.itemValue,
        )
    : []
})

const pending = computed(() => status.value === 'pending')

const currExclusionCategoryDropDownItem = ref<DropDownItem>({} as DropDownItem)

watch(exclusion, (newValue, oldValue) => {
  if (newValue) {
    currExclusionCategoryDropDownItem.value = {
      label: newValue?.category?.name ?? 'None',
      itemValue: newValue?.category?.id ?? 'id-none',
    }
  }

  if (newValue?.id && newValue.id !== oldValue?.id) {
    $pendo?.track('exclusion_detail_displayed', {
      product_area: 'exclusions',
      connector: workspace.connectorId,
      exclusion_id: newValue.id,
    })

    if (!Object.keys(selectedVersion.value).length) {
      selectedVersion.value = versionItems.value[0]
      /* the unversioned request already returns the current version, so
         only refetch when the auto-selected latest version is a scheduled
         future version */

      if (newValue.hasFutureVersion) refresh()
    }
  }
})

useHead(() => ({
  title: `${exclusion.value?.name || 'loading...'} – Exclusions`,
}))

const impactData = computed(() => {
  const exclusionFromProp = props.exclusions.find(
    ({ id }) => id === exclusionId.value,
  )

  return exclusionFromProp?.impact || null
})

watch([() => workspace.muxFilters], async () => {
  router.push(basePath.value)
})

watch(draftSelection, (newValue, _oldValue) => {
  // cases incoming in subsequent stories
  switch (newValue.itemValue) {
    case draftOperations.edit:
      exclusionState.setActiveExclusionEdit(exclusion.value ?? null)
      router.push(`/exclusions/${exclusionId.value}/edit`)
      break
    case draftOperations.delete:
      confirmDraftDelete.value = true
      break
    case draftOperations.create:
      confirmCreateExclusion.value = true
      break
    default:
      return
  }
})

onKeyStroke('Escape', (e) => {
  e.preventDefault()
  router.push(basePath.value)
})

const newExclusionName = ref('')

let newExclusionCategoryId: string | null | undefined = undefined

const existingExclusionNames = computed<Set<string>>(() => {
  if (!props.exclusions?.length) return new Set()

  const currentId = getExclusionId()

  return new Set(
    props.exclusions
      .filter(({ id }) => id !== currentId)
      .map(({ name }) => name?.trim())
      .filter((name): name is string => Boolean(name)),
  )
})

const isSameAsCurrentExclusionName = computed(() => {
  const trimmedName = newExclusionName.value.trim()

  if (!trimmedName) return false

  return trimmedName === (exclusion.value?.name?.trim() ?? '')
})

const isDuplicateExclusionName = computed(() => {
  const trimmedName = newExclusionName.value.trim()

  if (!trimmedName) return false

  return existingExclusionNames.value.has(trimmedName)
})

const exclusionNameError = computed(() => {
  const trimmedName = newExclusionName.value.trim()

  if (!trimmedName) return ''

  if (isSameAsCurrentExclusionName.value) {
    return 'This name matches the current exclusion name.'
  }

  if (isDuplicateExclusionName.value) {
    return 'An exclusion with this name already exists.'
  }

  return ''
})

const canSaveExclusionName = computed(
  () =>
    Boolean(newExclusionName.value.trim()) &&
    !isDuplicateExclusionName.value &&
    !isSameAsCurrentExclusionName.value,
)

const closeNameEditDialog = () => {
  confirmNameEdit.value = false
  newExclusionName.value = ''
}

const saveExclusionName = async () => {
  const id = getExclusionId()

  if (!id) return

  await editExclusion(
    { id, payload: { name: newExclusionName.value.trim() } },
    'confirmNameEdit',
  )
}

const updateExclusion = async (exclusionUpdates: ExclusionUpdate) => {
  const { id, payload } = exclusionUpdates

  try {
    isLoading.value = true
    await workersApi.fetch<WorkersApiLegacy.Exclusion>(`/exclusion/${id}`, {
      method: 'PUT',
      body: payload,
    })

    refresh()
    refreshNuxtData()
  } catch (error) {
    $groundcover?.captureException(error as Error)
  } finally {
    isLoading.value = false
  }
}

const editExclusion = async (
  exclusionUpdates: ExclusionUpdate,
  dialog: string,
) => {
  await updateExclusion(exclusionUpdates)
  dialogs[dialog].value = false
}

const applicableConnectors = computed(() => {
  const existing = exclusion.value?.exclusion_version_connectors ?? []

  if (!exclusion.value?.is_all_connectors) return existing

  const existingIds = new Set(existing.map((c) => c.connector_id))

  const missing = workspace.connectors
    .filter((c) => !existingIds.has(c.id))
    .map((c) => ({
      connector_id: c.id,
      status: 'Stopped',
      connector: { id: c.id, name: c.name, created_at: c.created_at },
    }))

  return [...existing, ...missing]
})

const activeConnectorStatus = computed(() => {
  const connectors = exclusion.value?.exclusion_version_connectors

  if (!connectors?.length) return ''

  if (workspace.connectorId === 'all_lob') {
    return connectors[0]?.status || ''
  }

  return (
    connectors.find(
      (connector) => connector.connector_id === workspace.connectorId,
    )?.status || ''
  )
})

const saveAllLobExclusionStatuses = async (
  exclusionUpdates: ExclusionUpdate,
) => {
  const { id, payload } = exclusionUpdates
  const updates = payload.connector_statuses

  if (!updates?.length) {
    statusEditorOpen.value = false

    return
  }

  try {
    isLoading.value = true
    const headers = await workersApi.getAuthHeaders()

    const response = await fetch(
      `/api/paapi/exclusion/${id}/connector-statuses`,
      {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ connector_statuses: updates }),
      },
    )

    if (!response.ok) {
      $toast.error('Failed to update exclusion statuses')

      return
    }

    refresh()
    refreshNuxtData()
    statusEditorOpen.value = false
    $toast.success('Exclusion statuses updated')
  } catch (error) {
    $toast.error('Failed to update exclusion statuses')
  } finally {
    isLoading.value = false
  }
}

const deleteDraft = async () => {
  isLoading.value = true

  try {
    await workersApi.fetch(`/exclusion/${exclusionId.value}`, {
      method: 'DELETE',
    })

    confirmDraftDelete.value = false
    $toast.success('Exclusion draft successfully deleted')
    router.push(basePath.value)
  } catch (error) {
    console.error('Error deleting exclusion', error)
  } finally {
    isLoading.value = false
  }
}

const createExclusionFromDraft = async () => {
  isLoading.value = true

  const body = {
    status: exclusionStatus.Draft,
  } as WorkersApiLegacy.CreateAExclusion

  try {
    await workersApi.fetch<WorkersApiLegacy.Exclusion>(
      `/exclusion/${exclusionId.value}`,
      {
        method: 'PUT',
        body,
      },
    )

    confirmCreateExclusion.value = false
    $toast.success('Exclusion successfully updated')
    router.push(basePath.value)
  } catch (error) {
    console.error('Error creating exclusion', error)
  } finally {
    isLoading.value = false
  }
}

const duplicateExclusion = () => {
  if (!exclusion.value) return

  exclusionState.setActiveExclusionDuplicate({
    ...exclusion.value,
    name: `(Copy) ${exclusion.value.name}`,
  })
  router.push('/exclusions/create')
}

const isSelectedVersionActive = computed(() =>
  exclusion.value?.hasFutureVersion
    ? selectedVersion.value.itemValue ===
      exclusion.value?.versions[exclusion.value?.versions.length - 2].version
    : selectedVersion.value.itemValue === lastVersion.value?.version,
)

const lastVersion = computed(
  () => exclusion.value?.versions[exclusion.value?.versions.length - 1],
)

const versionRangeText = computed(() => {
  const { start_date, end_date } = workspace.muxFilters

  const arrVersionsForTimeFrame =
    exclusion.value?.versions?.filter(
      (version: { version_start_date: string; version_end_date?: string }) =>
        isWithinInterval(new Date(version.version_start_date), {
          start: new Date(start_date),
          end: new Date(end_date),
        }) ||
        isWithinInterval(new Date(version.version_end_date!), {
          start: new Date(start_date),
          end: new Date(end_date),
        }) ||
        isWithinInterval(new Date(start_date), {
          start: new Date(version.version_start_date),
          end: new Date(version.version_end_date || todayDate.value), // for when Version End Date is null or "Forward"
        }),
    ) || []

  return arrVersionsForTimeFrame?.length > 1
    ? `Versions ${arrVersionsForTimeFrame[0]?.version}-${
        arrVersionsForTimeFrame[arrVersionsForTimeFrame?.length - 1]?.version
      } of this exclusion were Running over the selected time frame.`
    : arrVersionsForTimeFrame?.length === 1
      ? `Version ${arrVersionsForTimeFrame[0]?.version} of this exclusion was Running over the selected time frame.`
      : ''
})

const modifyModalHeaderText = computed(() =>
  exclusion.value?.hasFutureVersion
    ? 'Modify scheduled version change?'
    : 'Modify this exclusion and create new version?',
)

const modifyModalButtonText = computed(() =>
  exclusion.value?.hasFutureVersion
    ? 'Modify scheduled version change'
    : 'Modify this exclusion',
)

const modifyExclusion = async () => {
  if (!isSelectedVersionActive || exclusion.value?.hasFutureVersion) {
    const exclusionForModifying = await workersApi.fetch<
      PaAPI.Get['/exclusion/{id}']
    >(`/exclusion/${exclusionId.value}`, {
      params: {
        version: lastVersion.value?.version,
        include: true,
      },
    })

    exclusionState.setActiveExclusionEdit(exclusionForModifying as Exclusion)
    router.push(`/exclusions/${exclusionId.value}/edit`)
  } else {
    exclusionState.setActiveExclusionEdit(exclusion.value ?? null)
    router.push(`/exclusions/${exclusionId.value}/edit`)
  }
}

const openModifyDialog = () => {
  if (exclusion.value?.exclusion_status === exclusionStatus.Draft) {
    modifyExclusion()
  } else {
    confirmModifyDialogOpen.value = true
  }
}

const deleteScheduledVersion = async () => {
  isLoading.value = true

  try {
    const headers = await workersApi.getAuthHeaders()
    await fetch(`/api/paapi/exclusion/future/${exclusionId.value}`, {
      headers,
      method: 'DELETE',
    })

    confirmScheduledVersionDelete.value = false
    selectedVersion.value = {} as DropDownItem
    refresh()
  } catch (error) {
    console.error('Error deleting scheduled exclusion version change', error)
  } finally {
    isLoading.value = false
  }
}

const goToChangelog = (exclusionId: string) => {
  setUrlParams('exclusion', exclusionId)
  router.push('/changelog')
}

onUpdated(() => {
  newExclusionName.value = ''
  newExclusionCategoryId = undefined
})

const { data: exclusionCategories } = useLazyAsyncData<
  WorkersApiLegacy.ExclusionCategory[]
>(
  () => `/exclusion-category/${workspace.workspaceId}`,
  () => {
    if (!workspace.workspaceId) {
      return Promise.resolve([])
    }

    return workersApi.fetch(`/exclusion-category/${workspace.workspaceId}`)
  },
  {
    watch: [() => workspace.workspaceId],
  },
)

const exclusionCatItems = computed<DropDownItem[]>(() =>
  exclusionCategories.value?.length
    ? exclusionCategories.value.map(({ id, name: label }) => ({
        label,
        itemValue: (id === 'id-none' ? null : id) as string,
      }))
    : [],
)

const selectCategory = (newCategory: DropDownItem) => {
  newExclusionCategoryId =
    newCategory.itemValue === 'id-none'
      ? null
      : (newCategory.itemValue as string)
}
</script>

<template>
  <AppDetailsDrawer :data-analytics="analyticsTag('page-exclusion-detail')">
    <template #header>
      <header class="flex items-start justify-between border-b p-4 lg:px-8">
        <div>
          <div class="mb-2 flex items-center">
            <p
              class="h1 text-primary-900 min-h-7"
              :class="{
                'w-90 animate-pulse rounded bg-slate-200': pending,
              }"
            >
              {{ pending ? '' : exclusion?.name }}
            </p>
            <AuthPermissionsGuard
              v-if="workspace.isRialticUser"
              :permissions="[
                PERMISSIONS.writeSystem,
                PERMISSIONS.writeWorkspacesAll,
              ]"
              :workspace-id="workspace.workspaceId"
              :workspace-permissions="[
                WORKSPACE_PERMISSION.writeConnectorConfig,
              ]"
            >
              <UiButton
                class="ml-1 [&&]:focus:ring-0"
                type="button"
                variant="text"
                :data-analytics="analyticsTag('edit-exclusion-name')"
                @click="confirmNameEdit = true"
              >
                <div
                  class="i-heroicons-pencil-solid h-4 w-4 text-neutral-700"
                />
              </UiButton>
            </AuthPermissionsGuard>
          </div>
          <div class="flex items-center space-x-2">
            <UiButton
              v-if="exclusionId && workspace.canManageConnector"
              class="border-neutral-200 text-neutral-900 hover:border-neutral-200 hover:bg-neutral-50 hover:text-neutral-900"
              :disabled="exclusion?.exclusion_status === exclusionStatus.Draft"
              size="xs"
              variant="outlined"
              :data-analytics="analyticsTag('exclusion-status-button')"
              data-testid="exclusion_status"
              @click="statusEditorOpen = true"
            >
              <div class="inline-flex items-center justify-center space-x-2">
                <p>Statuses</p>
                <div
                  v-if="workspace.canManageConnector"
                  class="i-ph-arrows-counter-clockwise h-4 w-4"
                />
              </div>
            </UiButton>
            <div
              v-else
              class="w-30 h-9 animate-pulse rounded-lg bg-slate-200"
            />
            <UiButton
              v-if="
                workspace.canManageConnector &&
                exclusionId &&
                exclusion?.exclusion_status !== exclusionStatus.Archived
              "
              class="border-neutral-200 text-neutral-900 hover:border-neutral-200 hover:bg-neutral-50 hover:text-neutral-900"
              size="xs"
              variant="outlined"
              :data-analytics="analyticsTag('modify-exclusion-button')"
              data-testid="exclusion_modify"
              @click="openModifyDialog"
            >
              <div class="inline-flex items-center justify-center space-x-2">
                <p class="subtitle-2 capitalize">Modify</p>
                <div class="i-heroicons-pencil-solid h-4 w-4" />
              </div>
            </UiButton>
            <UiButton
              v-if="workspace.canManageConnector && exclusionId"
              class="border-neutral-200 text-neutral-900 hover:border-neutral-200 hover:bg-neutral-50 hover:text-neutral-900"
              :disabled="!isExclusionDuplicateEnabled"
              size="xs"
              variant="outlined"
              :data-analytics="analyticsTag('duplicate-exclusion-button')"
              data-testid="exclusion_duplicate"
              @click="duplicateExclusion"
            >
              <div class="inline-flex items-center justify-center space-x-2">
                <p class="subtitle-2 capitalize">Duplicate</p>
                <div class="i-ph-copy-light h-4 w-4" />
              </div>
            </UiButton>
            <UiButton
              v-if="
                workspace.canManageConnector &&
                exclusionId &&
                exclusion?.exclusion_status === exclusionStatus.Draft
              "
              class="bg-error-dark border-error-dark hover:bg-error-700 hover:border-error-700 text-white"
              size="xs"
              :data-analytics="analyticsTag('delete-draft-exclusion-button')"
              data-testid="exclusion_delete_draft"
              @click="confirmDraftDelete = true"
            >
              <div class="inline-flex items-center justify-center space-x-2">
                <p class="subtitle-2 capitalize">Delete</p>
                <div class="i-ph-trash h-4 w-4" />
              </div>
            </UiButton>
          </div>
        </div>
        <UiBtnClose
          :to="basePath"
          :data-analytics="analyticsTag('exclusion-close')"
        />
        <ExclusionsStatusSelectorAllLob
          v-if="exclusion && workspace.canManageConnector"
          v-model="statusEditorOpen"
          :connectors="applicableConnectors"
          :exclusion-id="exclusionId"
          :exclusion-name="exclusion?.name || ''"
          @save="saveAllLobExclusionStatuses"
        />
      </header>
    </template>
    <div
      class="h-full overflow-y-auto overscroll-contain scroll-smooth"
      tabindex="0"
    >
      <div class="mb-10 flex flex-col px-4 lg:px-8">
        <section class="flex flex-col space-y-2 pt-4">
          <h2 class="h2 text-neutral-900">Details</h2>
          <div class="flex items-center space-x-2">
            <UiDropdown
              v-model="selectedVersion"
              class="grow text-neutral-800"
              :items="versionItems"
              :data-analytics="analyticsTag('exclusion-version-dropdown')"
              @select="refresh()"
            >
              <template v-slot:default="{ modelValue }">
                <div
                  class="bg-surface flex w-full flex-1 items-center justify-between space-x-2 truncate rounded-lg border border-neutral-200 px-3 py-2 text-neutral-900"
                >
                  <div class="table-numbers-2 truncate">
                    <span class="text-primary-400 font-bold">
                      Version {{ modelValue?.itemValue }}
                    </span>
                    {{ modelValue?.label }}
                  </div>
                  <div class="i-ph-caret-down-bold h-4 w-4" />
                </div>
              </template>

              <template v-slot:option="{ item, modelValue, selectItem }">
                <button
                  class="hover:bg-surface-bg flex h-9 w-full items-center rounded-md p-2 hover:bg-neutral-100"
                  type="button"
                  :data-testid="`dropDown_${item.label
                    .replace(/\s/g, '-')
                    .toLowerCase()}`"
                  @click="selectItem(item)"
                >
                  <div
                    class="i-ph-check mr-2 h-5 w-5"
                    :class="
                      modelValue && modelValue.itemValue === item.itemValue
                        ? 'opacity-100'
                        : 'opacity-0'
                    "
                  />
                  <div class="table-numbers-2 text-primary-400 mr-1 font-bold">
                    Version {{ item.itemValue }}
                  </div>
                  <div
                    class="table-numbers-2 max-w-9/10 truncate text-neutral-900"
                  >
                    {{ item.label }}
                  </div>
                </button>
              </template>
            </UiDropdown>
            <UiButton
              class="border-neutral-200 text-neutral-900 hover:border-neutral-200 hover:bg-neutral-50 hover:text-neutral-900"
              size="xs"
              variant="outlined"
              :data-analytics="analyticsTag('exclusion-changelog-button')"
              @click="goToChangelog(exclusionId)"
            >
              <div class="inline-flex items-center justify-center space-x-2">
                <p>Changelog</p>
                <div
                  class="i-ph-clock-counter-clockwise h-5 w-5 text-neutral-900"
                />
              </div>
            </UiButton>
          </div>
          <div
            v-if="exclusion?.hasFutureVersion"
            class="flex flex-col space-y-1 rounded-lg bg-yellow-50 p-5"
          >
            <div class="flex items-center space-x-2">
              <div class="i-ph-calendar-blank-light h-6 w-6 text-yellow-700" />
              <div class="subtitle-2 text-neutral-900">
                Exclusion version change scheduled
              </div>
            </div>
            <div class="flex items-center space-x-1">
              <div class="table-numbers-2 text-primary-400 font-bold">
                Version
                {{
                  exclusion?.versions[exclusion?.versions?.length - 2]?.version
                }}
              </div>
              <div class="body-1 text-neutral-900">→</div>
              <div class="table-numbers-2 text-primary-400 font-bold">
                Version
                {{ lastVersion?.version }}
              </div>
              <div
                v-if="lastVersion?.version_start_date"
                class="body-2 text-neutral-900"
              >
                on
                {{
                  workspaceTimezone.format(lastVersion?.version_start_date, {
                    ...intlDateFormats.monDayYear,
                    ...intlDateFormats.timeWithTimeZoneName,
                  })
                }}
              </div>
              <div class="flex items-center space-x-1.5">
                <UiButton
                  class="btn-icon h-6 w-6"
                  size="xs"
                  variant="text"
                  :data-analytics="analyticsTag('modify-exclusion-button')"
                  @click="openModifyDialog"
                >
                  <i
                    class="i-heroicons-pencil-solid text-secondary-700 inline-block h-4 w-4"
                  />
                </UiButton>
                <UiButton
                  class="btn-icon h-6 w-6"
                  size="xs"
                  variant="text"
                  @click="confirmScheduledVersionDelete = true"
                >
                  <i
                    class="i-ph-trash-simple-fill text-secondary-700 inline-block h-4 w-4"
                  />
                </UiButton>
              </div>
            </div>
          </div>
          <h2 class="h2 pt-4 text-neutral-900">Exclusion Details</h2>
          <ExclusionsDetailsPanel
            :exclusion="exclusion as any"
            :pending="pending"
          />
        </section>
        <div class="my-8 h-px bg-neutral-200" />

        <ExclusionImpact
          v-if="impactData"
          :exclusion-id="exclusionId"
          :impact="impactData as any"
        />

        <footer v-if="versionRangeText" class="body-2 mt-4 text-neutral-700">
          {{ versionRangeText }}
        </footer>
      </div>
    </div>
    <UiDialog v-model="confirmNameEdit" class="p-4" max-width="416px">
      <header class="flex justify-end">
        <button
          class="btn btn-icon border-transparent p-0"
          @click="closeNameEditDialog"
        >
          <div class="i-ph-x-bold" />
        </button>
      </header>
      <div class="mb-7 mt-5 px-12 text-center">
        <div
          class="i-heroicons-pencil-solid text-secondary mx-auto mb-4 h-10 w-10"
        />
        <h2 class="h2 mb-2 text-neutral-900">Edit exclusion name</h2>
        <input
          v-model="newExclusionName"
          :class="[
            'border-1 w-full rounded-lg px-3 py-2 text-xs',
            { 'border-error-dark text-error-dark': exclusionNameError },
          ]"
          :placeholder="exclusion?.name"
        />
        <p v-if="exclusionNameError" class="text-error-dark mt-2 text-xs">
          {{ exclusionNameError }}
        </p>
      </div>
      <footer
        class="flex justify-center space-x-4"
        data-testid="exclusion_draft-update"
      >
        <UiButton
          class="hover:text-neutral-dark hover:bg-neutral hover:border-neutral-dark border-neutral-200 text-sm text-neutral-800"
          variant="outlined"
          @click="closeNameEditDialog"
        >
          Cancel
        </UiButton>
        <UiButton
          class="h-9 text-sm"
          :disabled="!canSaveExclusionName"
          :loading="isLoading"
          @click="saveExclusionName"
        >
          Save Edit
        </UiButton>
      </footer>
    </UiDialog>
    <UiDialog
      v-model="confirmCategoryEdit"
      class="p-4"
      decrease-z-index
      max-width="416px"
      persistent
    >
      <header class="flex justify-end">
        <button
          class="btn btn-icon border-transparent p-0"
          @click="confirmCategoryEdit = false"
        >
          <div class="i-ph-x-bold" />
        </button>
      </header>
      <div class="mb-7 mt-5 px-12 text-center">
        <div
          class="i-heroicons-pencil-solid text-secondary mx-auto mb-4 h-10 w-10"
        />
        <h2 class="h2 mb-2 text-neutral-900">Edit exclusion category</h2>
        <div class="">
          <div class="flex flex-col space-y-1">
            <UiDropdown
              v-if="exclusionCategories?.length"
              v-model="currExclusionCategoryDropDownItem"
              class="w-full text-neutral-800"
              :items="exclusionCatItems"
              placeholder="Select category"
              data-testid="exclusion_category_id"
              @select="selectCategory"
            />
          </div>
        </div>
      </div>
      <footer
        class="flex justify-center space-x-4"
        data-testid="exclusion_draft-update"
      >
        <UiButton
          class="hover:text-neutral-dark hover:bg-neutral hover:border-neutral-dark border-neutral-200 text-sm text-neutral-800"
          variant="outlined"
          @click="confirmCategoryEdit = false"
        >
          Cancel
        </UiButton>
        <UiButton
          class="h-9 text-sm"
          :disabled="newExclusionCategoryId === undefined"
          :loading="isLoading"
          @click="
            editExclusion(
              {
                id: exclusionId,
                payload: { exclusion_category_id: newExclusionCategoryId },
              },
              'confirmCategoryEdit',
            )
          "
        >
          Save Edit
        </UiButton>
      </footer>
    </UiDialog>
    <UiDialog v-model="confirmDraftDelete" class="p-4" max-width="416px">
      <header class="flex justify-end">
        <button
          class="btn btn-icon border-transparent p-0"
          @click="confirmDraftDelete = false"
        >
          <div class="i-ph-x-bold" />
        </button>
      </header>
      <div class="mb-7 mt-5 px-12 text-center">
        <div
          class="i-ph-warning-circle text-error-dark mx-auto mb-4 h-10 w-10"
        />
        <h2 class="h2 mb-2 text-neutral-900">Delete draft exclusion</h2>
        <span class="text-sm text-neutral-800">
          Are you sure you want to delete this draft exclusion?
        </span>
      </div>
      <footer
        class="flex justify-center space-x-4"
        data-testid="exclusion_draft-update"
      >
        <UiButton
          class="hover:text-neutral-dark hover:bg-neutral hover:border-neutral-dark border-neutral-200 text-sm text-neutral-800"
          variant="outlined"
          @click="confirmDraftDelete = false"
        >
          Cancel
        </UiButton>
        <UiButton
          class="bg-error-dark border-error-dark hover:bg-error-700 hover:border-error-700 h-9 text-sm"
          :loading="isLoading"
          @click="deleteDraft"
        >
          Delete draft
        </UiButton>
      </footer>
    </UiDialog>
    <UiDialog v-model="confirmCreateExclusion" class="p-4" max-width="416px">
      <header class="flex justify-end">
        <button
          class="btn btn-icon border-transparent p-0"
          @click="confirmCreateExclusion = false"
        >
          <div class="i-ph-x-bold" />
        </button>
      </header>
      <div class="mb-7 mt-5 px-11 text-center">
        <div class="i-ph-plus-circle text-secondary mx-auto mb-4 h-10 w-10" />
        <h2 class="h2 mb-2 text-neutral-900">Create this exclusion</h2>
        <span class="text-sm text-neutral-800">
          Creating this exclusion will make it able to exclude insights (when
          set to Running). Once created, the details of an exclusion cannot be
          edited.
        </span>
      </div>
      <footer class="flex justify-center space-x-4">
        <UiButton
          class="hover:text-neutral-dark hover:bg-neutral hover:border-neutral-dark border-neutral-200 text-sm text-neutral-800"
          variant="outlined"
          @click="confirmCreateExclusion = false"
        >
          Cancel
        </UiButton>
        <UiButton
          class="bg-secondary border-secondary hover:bg-secondary-700 hover:border-secondary-700 h-9 text-sm"
          :loading="isLoading"
          @click="createExclusionFromDraft"
        >
          Create this exclusion
        </UiButton>
      </footer>
    </UiDialog>
    <UiDialog
      v-if="workspace.canManageConnector"
      v-model="confirmModifyDialogOpen"
      class="p-4"
      max-width="416px"
    >
      <header class="flex justify-end">
        <button
          class="btn btn-icon border-transparent p-0"
          @click="confirmModifyDialogOpen = false"
        >
          <div class="i-ph-x-bold" />
        </button>
      </header>
      <div class="mb-7 mt-5 px-12 text-center">
        <div
          class="i-heroicons-pencil-solid text-secondary mx-auto mb-4 h-10 w-10"
        />
        <h2 class="h2 mb-2 text-neutral-900">
          {{ modifyModalHeaderText }}
        </h2>
        <template v-if="exclusion?.hasFutureVersion">
          <p class="body-1 text-neutral-800">
            You have already scheduled this exclusion to change to a new version
            in the future. Would you like to modify this upcoming version?
          </p>
          <p class="body-1 text-neutral-800">
            Note: not all fields can be modified.
          </p>
        </template>
        <template v-else>
          <p class="body-1 text-neutral-800">
            When you modify an exclusion, a new version of the exclusion will be
            created.
          </p>
          <p class="body-1 text-neutral-800">
            Note: not all fields can be modified.
          </p>
        </template>
      </div>
      <footer
        class="flex justify-center space-x-4"
        data-testid="exclusion_modify_dialog_footer"
      >
        <UiButton
          class="hover:text-neutral-dark hover:bg-neutral hover:border-neutral-dark border-neutral-200 text-sm text-neutral-800"
          variant="outlined"
          @click="confirmModifyDialogOpen = false"
        >
          Back
        </UiButton>
        <UiButton :loading="isLoading" size="sm" @click="modifyExclusion">
          {{ modifyModalButtonText }}
        </UiButton>
      </footer>
    </UiDialog>
    <UiDialog
      v-model="confirmScheduledVersionDelete"
      class="p-4"
      max-width="416px"
    >
      <header class="flex justify-end">
        <button
          class="btn btn-icon border-transparent p-0"
          @click="confirmScheduledVersionDelete = false"
        >
          <div class="i-ph-x-bold" />
        </button>
      </header>
      <div class="mb-7 mt-5 px-12 text-center">
        <div
          class="i-ph-warning-circle text-error-dark mx-auto mb-4 h-10 w-10"
        />
        <h2 class="h2 mb-2 text-neutral-900">
          Delete new version and scheduled change?
        </h2>
        <p class="body-1 mb-4 text-neutral-800">
          You have already created a new version and scheduled this exclusion to
          change to the new version in the future.
        </p>
        <p class="body-1 text-neutral-800">
          Would you like to delete this new version, and cancel the scheduled
          change?
        </p>
      </div>
      <footer
        class="flex justify-center space-x-4"
        data-testid="exclusion_scheduled-version-delete"
      >
        <UiButton
          class="hover:text-neutral-dark hover:bg-neutral hover:border-neutral-dark border-neutral-200 text-sm text-neutral-800"
          variant="outlined"
          @click="confirmScheduledVersionDelete = false"
        >
          Back
        </UiButton>
        <UiButton
          class="bg-error-dark border-error-dark hover:bg-error-700 hover:border-error-700 h-9 text-sm"
          :loading="isLoading"
          size="sm"
          @click="deleteScheduledVersion"
        >
          Delete version and cancel change
        </UiButton>
      </footer>
    </UiDialog>
  </AppDetailsDrawer>
</template>
